import { TransactionProvider } from '@/types/transaction.types';
import { TRANSACTION_NETWORKS, SYNC_CONFIG } from '@/config/transaction-networks';
import { getCustomNetworks } from '@/config/networks';
import { AlchemyProvider } from './providers/alchemy-provider';
import { ExplorerProvider } from './providers/explorer-provider';
import { NodeRealProvider } from './providers/nodereal-provider';
import { OMDBProvider } from './providers/omdb-provider';
import { GenericRPCProvider } from './providers/generic-rpc-provider';

export class TransactionProviderFactory {
  private static providers: Map<string, TransactionProvider> = new Map();
  private static configSnapshot: string = JSON.stringify(
    Object.fromEntries(Object.entries(TRANSACTION_NETWORKS).map(([k, v]) => [k, v.providerType]))
  );

  /**
   * Crea o retorna un provider existente para la red especificada
   */
  static getProvider(network: string): TransactionProvider {
    // Si el config cambió (HMR), limpiar cache para que se recreen los providers
    const currentSnapshot = JSON.stringify(
      Object.fromEntries(Object.entries(TRANSACTION_NETWORKS).map(([k, v]) => [k, v.providerType]))
    );
    if (currentSnapshot !== this.configSnapshot) {
      this.providers.clear();
      this.configSnapshot = currentSnapshot;
    }

    if (this.providers.has(network)) {
      return this.providers.get(network)!;
    }

    const config = TRANSACTION_NETWORKS[network];
    let provider: TransactionProvider;

    if (!config) {
      // Red personalizada: no está en la config fija, cae a RPC genérico.
      const customNetwork = getCustomNetworks().find((n) => n.id === network);
      if (!customNetwork) {
        throw new Error(`Network not supported: ${network}`);
      }
      provider = new GenericRPCProvider(network, customNetwork.rpcUrl);
      this.providers.set(network, provider);
      return provider;
    }

    switch (config.providerType) {
      case 'alchemy':
        if (!config.alchemyKey) {
          throw new Error(`Alchemy key not configured for network: ${network}`);
        }
        provider = new AlchemyProvider(network, config.alchemyKey, SYNC_CONFIG.REQUEST_TIMEOUT);
        break;

      case 'nodereal':
        if (!config.noderealKey) {
          throw new Error(`NodeReal key not configured for network: ${network}`);
        }
        provider = new NodeRealProvider(network, config.noderealKey, SYNC_CONFIG.REQUEST_TIMEOUT);
        break;

      case 'bscscan': // Alias de compatibilidad
      case 'explorer':
        if (network === 'omdb') {
          // OMDB tiene su propio provider con lógica específica
          if (!config.explorerApiUrl) {
            throw new Error(`Explorer API URL not configured for OMDB`);
          }
          provider = new OMDBProvider(config.explorerApiUrl, config.rpcUrl || '', SYNC_CONFIG.REQUEST_TIMEOUT);
        } else {
          // BSC → ExplorerProvider genérico (vía proxy del servidor)
          provider = new ExplorerProvider(network, SYNC_CONFIG.REQUEST_TIMEOUT);
        }
        break;

      case 'rpc':
        if (!config.rpcUrl) {
          throw new Error(`RPC URL not configured for network: ${network}`);
        }
        provider = new GenericRPCProvider(network, config.rpcUrl);
        break;

      default:
        throw new Error(`Provider type not supported: ${config.providerType}`);
    }

    this.providers.set(network, provider);
    console.log(`[TransactionProviderFactory] Created ${config.providerType} provider for ${network}`);

    return provider;
  }

  /**
   * Obtiene todos los providers disponibles
   */
  static getAllProviders(): Map<string, TransactionProvider> {
    // Inicializar todos los providers
    Object.keys(TRANSACTION_NETWORKS).forEach(network => {
      try {
        this.getProvider(network);
      } catch (error) {
        console.warn(`[TransactionProviderFactory] Could not create provider for ${network}:`, error);
      }
    });

    return this.providers;
  }

  /**
   * Verifica si una red está soportada
   */
  static isNetworkSupported(network: string): boolean {
    return network in TRANSACTION_NETWORKS || getCustomNetworks().some((n) => n.id === network);
  }

  /**
   * Obtiene la lista de redes soportadas, incluidas las personalizadas
   */
  static getSupportedNetworks(): string[] {
    return [...Object.keys(TRANSACTION_NETWORKS), ...getCustomNetworks().map((n) => n.id)];
  }

  /**
   * Limpia el cache de providers (útil para testing)
   */
  static clearCache(): void {
    this.providers.clear();
  }

  /**
   * Obtiene información de configuración de una red
   */
  static getNetworkConfig(network: string) {
    return TRANSACTION_NETWORKS[network];
  }
}